import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, useWatch } from "react-hook-form";

import { Accordion } from "iuvity-ui-components/dist/accordion";
import { Button } from "iuvity-ui-components/dist/button";
import { AccessButton } from "iuvity-ui-components/dist/accessbutton";
import { TabView } from "iuvity-ui-components/dist/tabview";
import { Tag } from "iuvity-ui-components/dist/tag";
import { Header } from "../../components";
import { CustomDialog, DialogProps } from "../../components/dialog/CustomDialog";
import { TransactionalSubheader } from "iuvity-ui-components/dist/transactionalsubheader";
import { BoxFull } from "iuvity-ui-components/dist/boxfull";
import { TreeSelection } from "iuvity-ui-components/dist//treeselection";

import { TableFunctionalities } from "../../components/roles/TableFunctionalities";
import { useFunctionalities } from "../../hooks/useFunctionalities";
import {
    IfunctionalitiesResponse,
    IpermissionsResponse,
    Permission,
    IcategoriesResponse,
    IpermissionsToInsert,
    RolePermission,
    ImasterCategoryResponse,
} from "../../interfaces/roles.interface";
import { usePermissions } from "../../hooks/usePermissions";
import { useCategories } from "../../hooks/useCategories";
import { useMasterCategory } from "../../hooks/useMasterCategory";
import { useRoles } from "../../hooks/useRoles";
import { AppContext } from "../../context";
import { CustomFormComponent } from "../../components/shared/CustomFormComponent";
import { RolesFormSchema } from "../../validations/schemas";
import {
    calendarTranslations,
    REVALIDATE_FORM_PROPS,
    rolesFieldsSelecter,
    ORDER_PERMISSIONS,
} from "../../constants";
import { formatStringToDate2 } from "../../helpers/formatDate";
import { categoryTranslator, roleTypeTranslator } from "../../helpers/roleTypeTranslations";
import { PrivilegeValidatorComponent } from "../../components/shared/PrivilegeValidatorComponent";

interface NewRolesPageProps {
    update?: boolean;
    clone?: boolean;
    detail?: boolean;
}

const formBlankValues = {
    tenantname: "",
    name: "",
    description: "",
    startDate: "",
    endDate: "",
  };

export const NewRolesPage: React.FC<NewRolesPageProps> = ({
    update= false,
    clone= false,
    detail=false,
}) => {

    const navigate = useNavigate();
    const {
        t,
        appState: { selectedRolesObject, tenantId, selectedApp, isMainTenant,
            tenantParentName, selectedTenantId, permissiondInfoByRole },
        setAppState,
      } = useContext(AppContext);
    const { data: masterCategoriesList }: { data: ImasterCategoryResponse[] } = useMasterCategory("DATA");
    const roleTypes:ImasterCategoryResponse[] = masterCategoriesList?.filter((category:ImasterCategoryResponse) => (
        category.status === 'ACTIVE' &&
        category.categoryId === 'ROLE_TYPES'));
    const roleTypesObject = {
        base: roleTypes?.find((category: ImasterCategoryResponse) => category.id.toLocaleLowerCase() === 'base'),
        client: roleTypes?.find((category: ImasterCategoryResponse) => (
            category.id.toLocaleLowerCase() === 'client' ||
            category.id.toLocaleLowerCase() === 'cliente'
        )),
    }
    const roleTypeId = (isMainTenant && !selectedTenantId) ? roleTypesObject.base?.id : roleTypesObject.client?.id;

    const permissionsTypesListBK:ImasterCategoryResponse[] = masterCategoriesList?.filter((category:ImasterCategoryResponse) => (
        category.status === 'ACTIVE' &&
        category.categoryId === 'PRIVILEGE_TYPES'));
    
    const formValues: {
        tenantname: string | undefined;
        description: string | undefined;
        startDate: string | undefined;
        endDate: string | undefined;
        name?: string | undefined;
        status?: string | undefined;
        nametoclone?: string | undefined;
        newname?: string | undefined;
    } = (update || detail) ? {
        tenantname: selectedApp
            ? selectedApp?.tenantname
            : (selectedRolesObject?.cameFromRolesPage
                ? selectedRolesObject?.tenantName
                : tenantParentName),
        name: selectedRolesObject?.name,
        description: selectedRolesObject?.description,
        startDate: detail
                    ? formatStringToDate2(selectedRolesObject?.expeditionDate || '', true, t)
                    : formatStringToDate2(selectedRolesObject?.expeditionDate || ''),
        endDate: detail
                    ? formatStringToDate2(selectedRolesObject?.expirationDate || '', true, t)
                    : formatStringToDate2(selectedRolesObject?.expirationDate || ''),
        status: selectedRolesObject?.statusId,
    } : clone
        ? {
            tenantname: selectedApp ? selectedApp?.tenantname : tenantParentName,
            nametoclone: selectedRolesObject?.name,
            newname: '',
            description: selectedRolesObject?.description,
            startDate: formatStringToDate2(selectedRolesObject?.expeditionDate || ''),
            endDate: formatStringToDate2(selectedRolesObject?.expirationDate || ''),
        } : { ...formBlankValues, tenantname: selectedApp ? selectedApp?.tenantname : tenantParentName }
    
    const form = useForm({
        mode: "onChange",
        delayError: 1200,
        reValidateMode: "onChange",
        // @ts-ignore
        defaultValues: formValues,
    });

    const {
        getValues,
        setValue,
        control,
        formState: { isDirty, isValid },
    } = form;

    const nameValues = useWatch({
        control,
        name: "name",
    });
    const descriptionValues = useWatch({
        control,
        name: "description",
    });
 
    const startDateValues = useWatch({
        control,
        name: "startDate",
    });
 
    const endDateValues = useWatch({
        control,
        name: "endDate",
    });
    const statusValues = useWatch({
        control,
        name: "status",
    });

    const rolesFields = rolesFieldsSelecter(
        update
            ? 'update'
            : clone
                ? 'clone'
                : detail
                    ? 'detail'
                    : 'default'
    );

    const { rolesAction, reloadFn } = useRoles("ACTIONS");

    let statusValuesObject: {label: string, value: string}[] = [
        {
            label: t('Roles.ACTIVELabel'),
            value: 'ACTIVE',
        },
        {
            label: t('Roles.INACTIVELabel'),
            value: 'INACTIVE',
        },
        {
            label: t('Roles.DRAFTLabel'),
            value: 'DRAFT',
        },
    ]

    statusValuesObject = selectedRolesObject?.statusId === 'DELETED' ? [ ...statusValuesObject, {
        label: t('Roles.DELETELabel'),
        value: 'DELETED',
    }] : statusValuesObject;

    const formKeysOptions:any = {
        status: statusValuesObject
        .sort((a: any, b: any) => a.label.localeCompare(b.label)),
    };

    const [modalProps, setModalProps] = useState<DialogProps>({
        visible: false,
    } as DialogProps);

    const onReturn = () => {
        setAppState((prev) => ({
            ...prev,
            selectedRolesObject: null,
        }));
        navigate('/adminroles');
        window.scrollTo(0, 0);
    };

    const onAction = async () => {
        const formValues = {
            ...getValues(),
            tenantId: selectedApp?.tenantid || selectedTenantId || tenantId,
            roleTypeId,
            rolePermissions: permissionsToInsert,
            expeditionDate: getValues('startDate') !== '' ? getValues('startDate') : undefined,
            expirationDate: getValues('endDate') !=='' ? getValues('endDate') : undefined,
            isAdministrative: roleTypeSelected === "1" ? true : false,
        };

        const actionResult = await rolesAction(update ? "UPDATE" : "CREATE",
            update
                ? {
                    ...formValues,
                    id: selectedRolesObject?.id,
                    statusId: formValues.status,
                }
                : clone
                    ? {
                        ...formValues,
                        name: formValues.newname,
                        newname: undefined,
                    }
                    : {
                        ...formValues,
                    });
        if (actionResult === "SUCCESS") {
            setAppState((prev) => ({
                ...prev,
                activeIndexPoints: 0,
            }));
            navigate("/adminroles");
            window.scrollTo(0, 0);
        }
    };

    const handleModalProps = (actionType: "action" | "cancel", goHome?: boolean) => {
        switch (actionType) {
        case "action":
            return setModalProps((prev) => ({
            ...prev,
            visible: true,
            title: t(`Roles.${update ? "save" : "create"}ModalTitle`),
            content: t(`Roles.${update ? "save" : "create"}ModalContent`).replace('tipo ', '').replace('type ', ''),
            onConfirm: onAction,
            }));
        case "cancel":
            return setModalProps((prev) => ({
            ...prev,
            visible: true,
            title: t("Roles.cancelModalTitle"),
            content: t("Roles.cancelModalContent"),
            onConfirm: goHome ? () => navigate('/') : onReturn,
            }));
        default:
            return;
        }
    };


    const optionalFieldsObject = {
        startDate: true,
        endDate: true,
    };

    const [calendarOptions, setCalendarOptions] = useState<any>({
        startDate: {
          value: undefined,
          minDate: new Date(),
          maxDate: undefined,
          disabled: false,
        },
        endDate: {
          value: undefined,
          minDate: new Date(),
          maxDate: undefined,
          disabled: ((update || clone) && selectedRolesObject?.expirationDate) ? false : true,
        },
    });

    const { data: permissionsListBK }: { data: IpermissionsResponse[] | undefined } = usePermissions("DATA");
    let permissionsObject:any = {}
    if (permissionsListBK && permissionsListBK.length > 0) {
        permissionsListBK.filter((item:Permission) => item.statusId === 'ACTIVE')
        // eslint-disable-next-line array-callback-return
        .map((permission:Permission) => {
            
            // // Formateo de permisos según funcionalidad, para obtener el id
            permissionsObject = {
                ...permissionsObject,
                [permission.privilegeId]: {
                    ...permissionsObject[permission.privilegeId],
                    [permission.functionalityId]: permission.id,
                }
            }
        });
    };

    // // VALIDACIÓN QUE EXISTAN OBLIGATORIAMENTE LOS 4 PERMISOS "CRUD"
    const prePermissionsList = ORDER_PERMISSIONS.filter((permission:string) => (
        Object.keys(permissionsObject).includes(permission)
    ));
    const permissionsList: string[] = prePermissionsList.length === ORDER_PERMISSIONS.length
        ? Object.keys(permissionsObject) : [];

    const { data: categoriesListBK }: { data: IcategoriesResponse[] | undefined } = useCategories("DATA");

    // // OBTENGO EL LISTADO DE FUNCIONALIDADES PARA MOSTRAR EN LA TABLA
    const { data: functionalitiesList }: { data: IfunctionalitiesResponse[] | undefined } = useFunctionalities("DATA");

    // // AÑADO FUNCIONALIDADES PADRES ACTIVAS A LAS CATEGORIAS ACTIVAS
    let functionalitiesSegmented:any = {
        applicationRole: {},
        adminRole: {},
    };

     // eslint-disable-next-line array-callback-return
     categoriesListBK?.map((children:IcategoriesResponse) => {
        functionalitiesSegmented = {
            applicationRole: {
              ...functionalitiesSegmented.applicationRole,
              [children.name]: [],
            },
            adminRole: {
                ...functionalitiesSegmented.adminRole,
                [children.name]: [],
              },
        };
    });
    
    // eslint-disable-next-line array-callback-return
    functionalitiesList?.map((functionality: IfunctionalitiesResponse) => {
            if (categoriesListBK && categoriesListBK.length > 0) {
                const auxArrayApplicationRole = functionalitiesSegmented.applicationRole.hasOwnProperty(functionality.category!.name!)
                    ? [...functionalitiesSegmented.applicationRole[functionality.category!.name!]]
                    : [];
                const auxArrayAdminRole = functionalitiesSegmented.adminRole.hasOwnProperty(functionality.category!.name!)
                    ? [...functionalitiesSegmented.adminRole[functionality.category!.name!]]
                    : [];
                functionalitiesSegmented = {
                    applicationRole: {
                        ...functionalitiesSegmented.applicationRole,
                        [functionality.category!.name!] : (functionality.statusId === 'ACTIVE' && !functionality.isAdministrative)
                            ? [ ...auxArrayApplicationRole, {...functionality} ]
                            : [...auxArrayApplicationRole],
                    },
                    adminRole: {
                        ...functionalitiesSegmented.adminRole,
                        [functionality.category!.name!] : (functionality.statusId === 'ACTIVE' && !!functionality.isAdministrative)
                            ? [ ...auxArrayAdminRole, {...functionality} ]
                            : [...auxArrayAdminRole],
                    },
                }
            }
    });
    

    // // ESTADO GLOBAL DE LOS CHECKBOXES PARA ARMAR EL ARRAY DE PERMISOS A SUBMITEAR
    // // ESTO PERMITE MANTENER LOS VISTOS A PESAR DE CERRAR LOS ACORDIONES O CAMBIAR EL TABVIEW
    const [checkedValue, setCheckedValue] = useState<any>({});
    const [ roleTypeSelected, setRoleTypeSelected ] = useState<string>(
        (update || clone || detail) ? (!!selectedRolesObject?.isAdministrative ? '1' : '0') : ''
    );

    let permissionsToInsertInitList: IpermissionsToInsert[] = [];
    let isAdministrative: boolean = false;
    // eslint-disable-next-line array-callback-return
    (update || clone || detail) && selectedRolesObject?.rolePermissions?.map((rolePermission:RolePermission) => {
        !!rolePermission?.functionality?.isAdministrative && (()=> isAdministrative = true)();
        permissionsToInsertInitList.push({
                functionalityId: rolePermission.permission.functionalityId,
                permissionId: rolePermission.permission.id,
            });
    });

    useEffect(()=>{
        if (update || clone || detail) {
            isAdministrative ? setRoleTypeSelected("1") : setRoleTypeSelected("0");
            setTimeout(()=>{
                document.getElementById(`tabview_header_${roleTypeSelected}`)?.click();
            }, 500);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[isAdministrative, roleTypeSelected, functionalitiesList, permissionsListBK, categoriesListBK, masterCategoriesList]);

    // // ARREGLO PARA GUARDAR LOS PERMISOS ELEGIDOS PARA EL ROL
    const [permissionsToInsert, setPermissionsToInsert] = useState<IpermissionsToInsert[]>(permissionsToInsertInitList);

    // // CREACIÓN DE OBJETO CON SUBCATEGORIAS (CATEGORIAS HIJAS)
    let subcategoriesListObject:any = {};
    // eslint-disable-next-line array-callback-return
    Object.keys(functionalitiesSegmented).map((categoryParent:string) => {
        // eslint-disable-next-line array-callback-return
        Object.keys(functionalitiesSegmented[categoryParent]).map((key) => {
            subcategoriesListObject = {
                ...subcategoriesListObject,
                [key]: 0
            }
        });
    });
    
    // // PERMISSIONS COUNTER STATE
    const [permissionsCounter, setPermissionsCounter] = useState<any>({});

    useEffect(()=>{
        if (Object.keys(permissionsCounter).length === 0) {
            setPermissionsCounter({ ...subcategoriesListObject, visited: {}});
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[Object.keys(subcategoriesListObject).length]);


    // // SI EL USUARIO CLICKEA ALGÚN PERMISO (PARA HABILITAR BOTÓN)
    const [permissionsChanged, setPermissionsChanged] = useState<boolean>(false);


    // // ACTIONS MODAL HANDLER
    const onDeleteAction = async (data: any) => {
        const actionResult = await rolesAction("DELETE", {id: data?.id, name: data?.name});
        if (actionResult === "SUCCESS") {
            setAppState((prev) => ({
                ...prev,
                selectedRolesObject: null,
            }));
            reloadFn();
            navigate("/adminroles");
        }
      };
    
      const onReactiveAction = async (data: any) => {
        const actionResult = await rolesAction("REACTIVE", {id: data?.id, name: data?.name});
        if (actionResult === "SUCCESS") {
            setAppState((prev) => ({
                ...prev,
                selectedRolesObject: null,
            }));
            reloadFn();
            navigate("/adminroles");
        }
      };
    
      const handleModalPropsVSdeleteReactive = async (type: 'DELETE' | 'REACTIVE', data: any) => {
        setModalProps((prev:any) => ({
          ...prev,
          visible: true,
          title: type === 'DELETE' ? t("Roles.deleteModalRolesTitle") : t("Roles.reactiveModalRolesTitle"),
          content: `${t("Roles.modalRolesContent1Label")} "${
            data?.name
          }" ${type === 'DELETE' ? t("Roles.deleteModalRolesContent2Label") : t("Roles.reactiveModalRolesContent2Label")}`,
          onConfirm: () => {
            type === 'DELETE' ? onDeleteAction(data!) : onReactiveAction(data!);
          },
        }));
      };
    
    const [lang, setLang] = useState<string>('');
    useEffect(() => {
        if (detail && !update && !clone) {
            const handleCallLanguageEvent = ((event: CustomEvent) => {
                setLang(localStorage.getItem('LANGUAGE') || '');
                setValue('startDate', formatStringToDate2(selectedRolesObject?.expeditionDate || '', true, t));
                setValue('endDate', formatStringToDate2(selectedRolesObject?.expirationDate || '', true, t));
            }) as EventListener;
            window.addEventListener(
                "CHANGE::LANGUAGE",
                handleCallLanguageEvent
            );
            return () =>
                window.removeEventListener(
                "CHANGE::LANGUAGE",
                handleCallLanguageEvent
                );
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lang, update, clone]);

    let categoriesObjectOrder:any = {};
    for (let key in functionalitiesSegmented) {
        const subcategoriesOrder = Object.keys(functionalitiesSegmented[key]).map((subcategory:string, index:number)=>(
            categoryTranslator(t, categoriesListBK, subcategory)
        )).sort((a: any, b: any) => a.localeCompare(b));
        categoriesObjectOrder = {
            ...categoriesObjectOrder,
            [key]: subcategoriesOrder
        }
    }
    const [ showTable, setShowTable ] = useState<boolean>(false);
    const [ activeIndex, setActiveIndex ] = useState<number>(0);
    const [ tabViewActiveIndex, setTabViewActiveIndex ] = useState<number>(-1);

    return (
        <div className="roles-management-main-section">
            {/* <Header
                welcomeMessage={t("Dashboard.welcomeTitle")}
                userName={user?.name}
            /> */}

            <TransactionalSubheader
                subheaderTitle={
                <div
                    className="roles-page-title"
                    style={{ position: "relative", left: "-90px" }}
                >
                    <div className="flex flex-row align-items-center">
                        <i
                            className="iu iu-icon-left pr-3"
                            onClick={() => {
                                selectedRolesObject?.cameFromRolesPage
                                    ? (() => {
                                        navigate('/users/edit', {
                                            state: { from: window.location.pathname + (
                                                selectedRolesObject?.isAdministrative ? '?isadmin' : ''
                                            ) }
                                        });
                                      })()
                                    : ((isDirty  || permissionsChanged) ? handleModalProps("cancel") : onReturn())
                            }}
                            aria-hidden="true"
                            style={{
                            fontSize: "var(--font-size-tablet-title)",
                            cursor: "pointer",
                            }}
                        />
                        <div className="flex alig-items-center">
                            {
                                update
                                ? t("Roles.editRolesTitle")
                                : detail
                                    ? t("Roles.detailRolesTitle")
                                    : clone
                                        ? t("Roles.cloneRolesTitle")
                                        : t("Roles.createRolesTitle")
                            }
                            {
                            (!clone)
                                ?
                                <Tag
                                    className={'flow-badge ml-3'}
                                    value={
                                        selectedRolesObject?.statusId === 'DELETED'
                                            ? t('Roles.DELETELabel')
                                            : (update || clone || detail)
                                                ? roleTypeTranslator(t, roleTypesObject, selectedRolesObject)
                                                // @ts-ignore
                                                : roleTypeTranslator(t, roleTypesObject, {roleTypeId})
                                    }
                                    style={{
                                        textTransform: 'none',
                                        color: "var(--cardBackgroundColorOneShade80)",
                                    }}
                                />  
                                : null
                            }
                        </div>
                    </div>
                    {
                        detail && selectedRolesObject?.cameFromRolesPage
                        ?   <div
                                className="roles-page-title__detail"
                                style={{
                                    marginLeft: '30px',
                                }}
                            >
                                <p
                                    className="roles-page-title__detail--text"
                                    dangerouslySetInnerHTML={{
                                        __html: t("Roles.cameFromUserPageDetailDescription")
                                    }}
                                    style={{ lineHeight: '1.5' }}
                                />
                            </div>
                        :   <div
                                className="roles-page-title__detail"
                                style={{
                                    marginLeft: '30px',
                                }}
                            >
                                <p
                                    className="roles-page-title__detail--text"
                                    dangerouslySetInnerHTML={{
                                        __html: update
                                            ? t("Roles.editRolesDescription")
                                            : detail
                                                ? (selectedRolesObject?.statusId === 'DELETED'
                                                    ? t("Roles.detailRolesDeleteDescription")
                                                    : (selectedRolesObject?.roleTypeId === 'BASE'
                                                        ? (isMainTenant
                                                            ? t("Roles.detailRolesClientDescription")
                                                            : t("Roles.detailRolesDescription"))
                                                        : t("Roles.detailRolesClientDescription")))
                                                : clone
                                                    ? t("Roles.cloneRolesDescription")
                                                    : t("Roles.createRolesDescription")
                                    }}
                                    style={{ lineHeight: '1.5' }}
                                />
                            </div>
                    }
                </div>
                }
            >
                {
                    !selectedRolesObject?.cameFromRolesPage ?
                        <TransactionalSubheader.BreadCrumb
                        id={t(`Roles.${update ? 'edit' : detail ? 'detail' : clone ? 'clone' : 'create'}RolesTitle`)}
                        disabledDefaultHome={true}
                        breadcrumbsHomeItem={{
                            icon: "iu iu-icon-home",
                            command: () => isDirty ? handleModalProps("cancel", true) : navigate('/'),
                        }}
                        breadcrumbsItems={[
                            {
                                label: t("ConsoleTranslations.accessControlBreadcrumbs"),
                                disabled: true,
                            },
                            {
                                label: t("Roles.rolesManagementBreadcrumbs"),
                                style: { fontFamily: "var(--fontBarlowSemiBold)" },
                                command: () => (isDirty || permissionsChanged) ? handleModalProps("cancel") : onReturn(),
                            },
                            {
                                label: t(`Roles.${update ? 'edit' : detail ? 'detail' : clone ? 'clone' : 'create'}RolesBreadcrumb`),
                                disabled: true
                            },
                        ]}
                        style={{
                            paddingLeft: '0',
                        }}
                        />
                    : <div className="mb-5"/>
                }
                <TransactionalSubheader.Title />
            </TransactionalSubheader>
            {
                detail && !selectedRolesObject?.cameFromRolesPage &&
                <BoxFull className="m-0 px-0 pb-0 pt-2 roles-actions-boxfull" variant="tertiary">
                    <div className="tablefilter-mc2">
                        {
                            selectedRolesObject?.statusId !== 'DELETED' ?
                            <>
                                <PrivilegeValidatorComponent showButton={permissiondInfoByRole.CREATE}>
                                    <AccessButton
                                        id='roles-clone-access-button'
                                        icon="iu iu-icon-copy"
                                        label={t("Roles.cloneRolesTitle")}
                                        onClick={() => {
                                            setValue('nametoclone', selectedRolesObject?.name);
                                            setValue('startDate', formatStringToDate2(selectedRolesObject?.expeditionDate || ''));
                                            setValue('endDate', formatStringToDate2(selectedRolesObject?.expirationDate || ''));
                                            setCalendarOptions((prev:any)=>({
                                                ...prev,
                                                endDate: {
                                                    ...prev.endDate,
                                                    disabled: selectedRolesObject?.expirationDate ? false : true,
                                                }
                                            }));
                                            navigate("/adminroles/cloneroles");
                                        }}
                                    />
                                </PrivilegeValidatorComponent>
                                    {
                                        (roleTypeId?.toLowerCase() !== 'base' &&
                                        selectedRolesObject?.roleTypeId.toLowerCase() === 'base')
                                        ?   null
                                        :   <PrivilegeValidatorComponent showButton={permissiondInfoByRole.UPDATE} >
                                                <AccessButton
                                                    id='roles-edit-access-button'
                                                    icon="iu iu-icon-edit"
                                                    label={t("Roles.editRolesTitle")}
                                                    onClick={() => {
                                                        setValue('startDate', formatStringToDate2(selectedRolesObject?.expeditionDate || ''));
                                                        setValue('endDate', formatStringToDate2(selectedRolesObject?.expirationDate || ''));
                                                        setCalendarOptions((prev:any)=>({
                                                            ...prev,
                                                            endDate: {
                                                                ...prev.endDate,
                                                                disabled: selectedRolesObject?.expirationDate ? false : true,
                                                            }
                                                        }));
                                                        navigate("/adminroles/editroles");
                                                    }}
                                                />
                                        </PrivilegeValidatorComponent>
                                    }
                            </> : null
                        }
                    </div>
                </BoxFull>
            }
            <div
                key='role-main-container'
                style={{
                    marginTop: detail ? (
                        selectedRolesObject?.cameFromRolesPage
                            ? 'var(--space-l)'
                            : 'var(--space-s)'
                    ) : 'var(--space-xl)',
                    marginRight: '128px',
                    marginLeft: '128px'
                }}
                className="role-main-container"
            >
                <div
                    className="actions-roles-description-box"
                >
                    <div className="actions-roles-description-box__container">
                        <div className="actions-roles-description-box__container--title">
                            <p className="boxTitle boxTitle--roles">
                                {t("Roles.rolesInformationTitle")}
                            </p>
                        </div>
                    </div>
                </div>
                <div
                    key='form-section'
                    className="form-section"
                >
                    <CustomFormComponent
                        functionality=   "Roles"
                        fields = {rolesFields}
                        form = {form}
                        dropdownOptions = {formKeysOptions}
                        schemaOfValidation = {RolesFormSchema}
                        language = {localStorage.getItem('LANGUAGE') || 'en'}
                        isOptional={optionalFieldsObject}
                        calendarTranslations = {calendarTranslations}
                        calendarOptions = {calendarOptions}
                        setCalendarOptions = {setCalendarOptions}
                        REVALIDATE_FORM_PROPS = {REVALIDATE_FORM_PROPS}
                        readOnlyObject = {{
                            tenantname: true,
                            name: detail ? true : false,
                            nametoclone: clone ? true : false,
                            description: detail ? true : false,
                            startDate: detail ? true : false,
                            endDate: detail ? true : false,
                            status: detail ? true : false,
                        }}
                        isDetail = {detail}
                        tooltipTextObject = {{
                            name: t('Roles.nameTooltip'),
                            newname: t('Roles.nameTooltip'),
                        }}
                    />
                </div>
                <div
                    className="actions-roles-description-box"
                    style={{
                        marginBottom: !(update || clone || detail) ? 'var(--space-xl)' : 0,
                    }}
                >
                    <div className="actions-roles-description-box__container">
                        <div className="actions-roles-description-box__container--title flex">
                            <p className="boxTitle boxTitle--roles">
                                {t("Roles.rolesActionsTitle")}
                            </p>
                            {
                                (update || clone || detail) &&
                                <Tag
                                    className={'flow-badge ml-3'}
                                    value={isAdministrative ? t('Roles.adminRoleTitle') : t('Roles.applicationRoleTitle')}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        color: "var(--cardBackgroundColorOneShade80)",
                                        height: "25px",
                                        marginTop: "2px",
                                    }}
                                />
                            }
                        </div>
                        <div className="actions-roles-description-box__container--detail">
                            {
                                !detail &&
                                <p
                                    style={{
                                        fontFamily: "var(--fontBarlowMedium)",
                                    color: "var(--cardBackgroundColorOneShade80)",
                                    }}
                                >
                                    {t("Roles.rolesActionsDescription")}
                                </p>
                            }
                            {
                                !(update || clone || detail) &&
                                <div
                                    className="m-5 py-5"
                                    style={{
                                        backgroundColor: "var(--cardBackgroundColorTwo)",
                                        borderRadius: "0.5rem",
                                        display: "flex",
                                        justifyContent: "center",
                                    }}
                                >
                                    <div className="flex justify-content-around flex-wrap" style={{ width: "90%" }}>
                                        <div style={{ minWidth: "275px", width: "10%", maxWidth: "100%" }}>
                                            <div className="w-full">
                                                <TreeSelection
                                                    id='adminRolesContainer'
                                                    children={<></>}
                                                    value={[
                                                        {   id: "0",
                                                            label: t('Roles.applicationRoleTitle'),
                                                            className:
                                                                `${roleTypeSelected === "0" ? "role-card-selected" : ""}
                                                                ${(update || clone || detail)
                                                                    ? (isAdministrative ? " p-disabled" : "")
                                                                    : ((permissionsToInsert.length > 0 && roleTypeSelected !== "0") ? " p-disabled" : "")
                                                                }`
                                                        },
                                                        {   id: "1",
                                                            label: t('Roles.adminRoleTitle'),
                                                            className:
                                                                `${roleTypeSelected === "1" ? "role-card-selected" : ""}
                                                                ${(update || clone || detail)
                                                                    ? (!isAdministrative ? " p-disabled" : "")
                                                                    : ((permissionsToInsert.length > 0 && roleTypeSelected !== "1") ? " p-disabled" : "")
                                                                }`
                                                        }]}
                                                    selectionMode='single'
                                                    selectionKeys={"MMMM"}
                                                    onSelect={(e: any) => {
                                                        document.getElementById(`tabview_header_${e?.node?.id || "0"}`)?.click();
                                                        setRoleTypeSelected(e?.node?.id);
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div style={{ minWidth: "275px", width: "40%", maxWidth: "100%" }}>
                                            <div
                                                id="roles-tooltip-box"
                                                className="w-full"
                                                style={{
                                                    display: "flex",
                                                }}
                                            >
                                                <i className="help-name-application iu iu-icon-info act-btn mr-2" />
                                                <p
                                                    className='text-description-name'
                                                    style={{
                                                        fontSize: "var(--font-size-sm)",
                                                        color: "var(--cardBackgroundColorOneShade60)",
                                                    }}
                                                >
                                                    <div dangerouslySetInnerHTML={{ __html: t('Roles.actionsRoleTypeDescription') }} />
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                </div>
                <div key='tabviews-container'>
                    {
                        Object.keys(functionalitiesSegmented).length > 0 && Object.keys(permissionsObject).length > 0 &&
                        <TabView
                            key='tabview-role-main-container'
                            className="admin-roles-tabview"
                            activeIndex={tabViewActiveIndex}
                            items={Object.keys(functionalitiesSegmented).map((category, index)=>(
                                {
                                    key: `tabview-${index}`,
                                    header: category,
                                    children:(
                                        <div key={`tab-view-children-${index}`}>
                                            {
                                                !detail &&
                                                <div
                                                    dangerouslySetInnerHTML={{ __html: t('Roles.functionalitiesListDescription') }}
                                                    style={{
                                                        transform: "translate(-16px)",
                                                        marginBottom: "40px",
                                                        fontFamily: "var(--fontBarlowMedium)",
                                                        color: "var(--cardBackgroundColorOneShade80)",
                                                    }}
                                                />
                                            }
                                            {
                                                Object.keys(functionalitiesSegmented[category])
                                                ?.map((subcategory) => ({
                                                    subcategory,
                                                    label: categoryTranslator(t, categoriesListBK, subcategory),
                                                }))
                                                ?.sort((a: any, b: any) => {
                                                    return categoriesObjectOrder[category].indexOf(a.label) - categoriesObjectOrder[category].indexOf(b.label);
                                                  })
                                                    ?.map(({subcategory, label}: {subcategory: string, label:string}) => {
                                                        return (functionalitiesSegmented[category][subcategory].length > 0) &&
                                                            (<Accordion
                                                                key={`accordion-container-${subcategory}`}
                                                                id={`functionalities-accordion-multiple-${subcategory}`}
                                                                activeIndex={
                                                                    Object.keys(functionalitiesSegmented[category])
                                                                    .filter((subcategory:any) => functionalitiesSegmented[category][subcategory].length > 0)
                                                                    .length === 1
                                                                        ? 0
                                                                        : activeIndex
                                                                }
                                                                items= {[{
                                                                    children:
                                                                        <TableFunctionalities
                                                                            t={t}
                                                                            key={subcategory}
                                                                            dataCategory={
                                                                                functionalitiesSegmented[category][subcategory]
                                                                            }
                                                                            subcategory={subcategory}
                                                                            checkedValue={checkedValue}
                                                                            setCheckedValue={setCheckedValue}
                                                                            dataRole={(update || detail || clone) ? selectedRolesObject! : undefined}
                                                                            permissionsList={permissionsList}
                                                                            permissionsToInsert={permissionsToInsert}
                                                                            setPermissionsToInsert={setPermissionsToInsert}
                                                                            permissionsWithIDS={permissionsObject}
                                                                            permissionsCounter={permissionsCounter}
                                                                            setPermissionsCounter={setPermissionsCounter}
                                                                            checksDisable={detail}
                                                                            permissionsToInsertInitList={permissionsToInsertInitList}
                                                                            setPermissionsChanged={setPermissionsChanged}
                                                                            permissionsTypesListBK={permissionsTypesListBK}
                                                                            showTable={
                                                                                Object.keys(functionalitiesSegmented[category])
                                                                                .filter((subcategory:any) => functionalitiesSegmented[category][subcategory].length > 0)
                                                                                .length === 1
                                                                                    ? true
                                                                                    : showTable
                                                                            }
                                                                            setActiveIndex={setActiveIndex}
                                                                        />,
                                                                    disabled: false,
                                                                    headerTemplate: `${categoryTranslator(
                                                                        t, categoriesListBK, subcategory
                                                                    )} ( ${permissionsCounter[subcategory]} )`,
                                                                    key: `accordion-${subcategory}`,
                                                                }]}
                                                                style={{
                                                                    margin: '0 var(--padding-level-twenty-four)',
                                                                }}
                                                                onTabOpen={() => setShowTable(true)}
                                                            />)
                                                    })
                                            }
                                        </div>
                                    ),
                                }
                            ))}
                            onTabChange={(e) => {
                                setTabViewActiveIndex(e.index);
                                setShowTable(false);
                                setActiveIndex(0);
                            }}
                        />
                    }
                </div>
            </div>

            {/* Buttons */}
            <div className="roles-management-buttons">
                {
                    selectedRolesObject?.cameFromRolesPage
                    ?   <div
                            className="ap-selector-btn-c my-5 roles-management-buttons__button-container"
                        >
                            <Button
                                variant="p-button"
                                label={t(`Roles.returnToUsersBtnLabel`)}
                                onClick={() => {
                                    navigate('/users/edit', {
                                        state: { from: window.location.pathname + (
                                            selectedRolesObject?.isAdministrative ? '?isadmin' : ''
                                        ) }
                                    });
                                }}
                            />
                        </div>
                    :   <div
                        className="ap-selector-btn-c my-5 gap-4 roles-management-buttons__button-container"
                        >
                        {
                            selectedRolesObject?.statusId !== 'DELETED' &&
                            <Button
                                variant="p-button-text"
                                label={t("Roles.cancelBtnLabel")}
                                onClick={
                                    update
                                    ? 
                                        !((
                                            (isDirty &&
                                            (
                                            nameValues !== selectedRolesObject?.name ||
                                            descriptionValues !== selectedRolesObject?.description ||
                                            formatStringToDate2(startDateValues || '', true) !== formatStringToDate2(selectedRolesObject?.expeditionDate || '', true) ||
                                            formatStringToDate2(endDateValues || '', true) !== formatStringToDate2(selectedRolesObject?.expirationDate || '', true) ||
                                            statusValues !== selectedRolesObject?.statusId
                                            )) || permissionsChanged
                                        ) &&
                                            isValid)
                                            ? onReturn
                                            : () => handleModalProps("cancel")
                                    : detail
                                            ? onReturn
                                            : (!isDirty && !permissionsChanged) ? onReturn : () => handleModalProps("cancel")
                                }
                            />
                        }
                        {
                            permissiondInfoByRole.REMOVE && detail &&
                            (
                                (roleTypeId?.toLowerCase() === 'client' && selectedRolesObject?.roleTypeId?.toLowerCase() === 'client') ||
                                (roleTypeId?.toLowerCase() === 'cliente' && selectedRolesObject?.roleTypeId?.toLowerCase() === 'cliente') ||
                                (roleTypeId?.toLowerCase() === 'base' && selectedRolesObject?.roleTypeId?.toLowerCase() === 'base')
                            ) &&
                            selectedRolesObject?.statusId !== 'DELETED' &&
                            <Button
                                variant="p-button-text"
                                label={t("Roles.deleteBtnLabel")}
                                onClick={() => handleModalPropsVSdeleteReactive('DELETE', selectedRolesObject)}
                            />
                        }
                        {
                            permissiondInfoByRole.CREATE && detail &&
                            selectedRolesObject?.statusId !== 'DELETED' &&
                            <Button
                                variant={
                                    (roleTypeId?.toLowerCase() !== 'base' &&
                                    selectedRolesObject?.roleTypeId?.toLowerCase() === 'base')
                                    ? "p-button" : "p-button-text"
                                }
                                label={t("Roles.cloneBtnLabel")}
                                onClick={() => {
                                    setValue('nametoclone', selectedRolesObject?.name);
                                    setValue('startDate', formatStringToDate2(selectedRolesObject?.expeditionDate || ''));
                                    setValue('endDate', formatStringToDate2(selectedRolesObject?.expirationDate || ''));
                                    setCalendarOptions((prev:any)=>({
                                        ...prev,
                                        endDate: {
                                            ...prev.endDate,
                                            disabled: selectedRolesObject?.expirationDate ? false : true,
                                        }
                                    }));
                                    navigate("/adminroles/cloneroles");
                                    window.scrollTo(0,0);
                                }}
                            />
                        }
                        {
                            (
                                (roleTypeId?.toLowerCase() === 'client' && selectedRolesObject?.roleTypeId?.toLowerCase() === 'client') ||
                                (roleTypeId?.toLowerCase() === 'cliente' && selectedRolesObject?.roleTypeId?.toLowerCase() === 'cliente') ||
                                (roleTypeId?.toLowerCase() === 'base' && selectedRolesObject?.roleTypeId?.toLowerCase() === 'base') ||
                                clone || !(update || detail || clone)
                            ) &&
                            <PrivilegeValidatorComponent showButton={!((update && !permissiondInfoByRole.UPDATE)
                                || (detail && selectedRolesObject?.statusId !== 'DELETED' && !permissiondInfoByRole.UPDATE)
                                || (clone && !permissiondInfoByRole.CREATE)
                            )}>
                                <Button
                                    variant="p-button"
                                    label={t(`Roles.${update
                                        ? 'save'
                                        : (detail && selectedRolesObject?.statusId === 'DELETED')
                                            ? 'back'
                                            : (detail && selectedRolesObject?.statusId !== 'DELETED')
                                                ? 'edit'
                                                : clone
                                                    ? 'clone'
                                                    : 'create'}BtnLabel`)}
                                    disabled={
                                        update
                                        ?
                                        !((
                                            (isDirty &&
                                            (
                                            nameValues !== selectedRolesObject?.name ||
                                            descriptionValues !== selectedRolesObject?.description ||
                                            formatStringToDate2(startDateValues || '', true) !== formatStringToDate2(selectedRolesObject?.expeditionDate || '', true) ||
                                            formatStringToDate2(endDateValues || '', true) !== formatStringToDate2(selectedRolesObject?.expirationDate || '', true) ||
                                            statusValues !== selectedRolesObject?.statusId
                                            )) || permissionsChanged
                                        ) &&
                                            isValid)
                                        :
                                            !(
                                            isValid
                                            )
                                    }
                                    onClick={() => {
                                        if (detail) {
                                            if (selectedRolesObject?.statusId !== 'DELETED') {
                                                navigate("/adminroles/editroles");
                                                setValue('startDate', formatStringToDate2(selectedRolesObject?.expeditionDate || ''));
                                                setValue('endDate', formatStringToDate2(selectedRolesObject?.expirationDate || ''));
                                                setCalendarOptions((prev:any)=>({
                                                    ...prev,
                                                    endDate: {
                                                        ...prev.endDate,
                                                        disabled: selectedRolesObject?.expirationDate ? false : true,
                                                    }
                                                }));
                                                setTimeout(()=>{
                                                    window.scrollTo(0,0);
                                                },50);
                                            }
                                            else {
                                                onReturn();
                                            }
                                        }
                                        else handleModalProps("action");
                                    }}
                                />
                            </PrivilegeValidatorComponent>
                        }
                        </div>
                }
            </div>

            <CustomDialog
                title={modalProps.title}
                visible={modalProps.visible}
                content={modalProps.content}
                handleModalState={setModalProps}
                onConfirm={modalProps.onConfirm}
            />
        </div>
    )
}
